<template>
  <div class="formBuilder">
    <div class="headPanel">
      <div class="formControl">
        <v-select
          dense
          outlined
          :items="formsList"
          v-model="selectedForm"
          label="Select form"
          full-width
        ></v-select>
      </div>
      <div v-if="selectedForm">
        <div v-if="currentPlanId" class="">
          <v-checkbox
            v-model="planLevel"
            label="Plan level"
            class="mt-0 pt-0 planLevelCheckbox"
          ></v-checkbox>
          <span v-if="planLevel" class="currentPlanName">{{ currentPlanName }}</span>
        </div>
        <div v-else>
          <v-alert type="info" outlined dense>
            Please, select a plan first to view the plan-level grid
          </v-alert>
        </div>
      </div>
    </div>
    <div class="builderTable" v-if="selectedForm">
      <v-data-table
        :headers="headers"
        :items="formElements"
        :loading="loading"
        :search="search"
        :items-per-page="-1"
        @click:row="editFieldRow"
        dense
      >
        <template v-slot:top>
          <div class="tableToolbar">
            <v-dialog persistent v-model="isDialogShown" max-width="750">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  depressed
                  v-bind="attrs"
                  v-on="on"
                  :disabled="loading || !showFormButtons"
                  class="mb-2"
                  @click="editFieldRow(defaultItem)"
                >
                  Add field
                </v-btn>
              </template>

              <v-card>
                <v-card-title>
                  <span class="text-h5">{{ dialogTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <div>
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="editedItem.FieldId"
                          label="Field"
                          :readonly="editedIndex >= 0"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field v-model="editedItem.Label" label="Label"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-select
                          v-model="editedItem.FieldType"
                          :items="fieldTypeList"
                          label="Field type"
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field v-model="editedItem.FieldOrder" label="Order"></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-checkbox v-model="editedItem.Visible" label="Visible"></v-checkbox>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-checkbox v-model="editedItem.Mandatory" label="Mandatory"></v-checkbox>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="editedItem.ListItems"
                          label="List/Lookup Items"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="editedItem.Attributes"
                          label="Field Attributes"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="editedItem.FieldFormat"
                          label="Field Format"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="editedItem.ParentContainer"
                          label="Panel"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-select
                          v-model="editedItem.ParentTab"
                          :items="parentTabsList"
                          item-text="FieldName"
                          item-value="FieldId"
                          label="Tab"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-btn
                    v-if="editedItem.FieldType === 'Tab' || editedItem.FieldType === 'Panel'"
                    color="error"
                    outlined
                    @click="deleteItem"
                    >Delete</v-btn
                  >
                  <v-spacer></v-spacer>
                  <v-checkbox 
                  v-if="!planLevel"
                  class="pt-5"
                  v-model="overwritePlanLevel"
                  label="Overwrite Plan Level"
                  >
                  </v-checkbox>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" outlined @click="cancelSave"> Cancel </v-btn>
                  <v-btn color="primary" depressed @click="saveItem"> Save </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              class="mb-4 search"
              single-line
              hide-details
            ></v-text-field>
          </div>
        </template>

        <template v-slot:[`item.Visible`]="{ item }">
          <v-simple-checkbox v-model="item.Visible" disabled></v-simple-checkbox>
        </template>
        <template v-slot:[`item.Mandatory`]="{ item }">
          <v-simple-checkbox v-model="item.Mandatory" disabled></v-simple-checkbox>
        </template>
      </v-data-table>
    </div>
    <Dialog ref="confirmationDialog" />
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'FormBuilder',
  components: { Dialog },
  props: {},
  data() {
    return {
      loading: false,
      planLevel: false,
      overwritePlanLevel: false,
      formsList: [],
      selectedForm: null,
      search: '',
      headers: [
        { text: 'Field', value: 'FieldId' },
        { text: 'Label', value: 'Label' },
        { text: 'Visible', value: 'Visible' },
        { text: 'Mandatory', value: 'Mandatory' },
        { text: 'Order', value: 'FieldOrder' },
        { text: 'Plan', value: 'PlanId' },
        { text: 'Field Type', value: 'FieldType', filterable: false },
        { text: 'List/Lookup Items', value: 'ListItems', filterable: false },
        { text: 'Field Attributes', value: 'Attributes', filterable: false },
        { text: 'Field Format', value: 'FieldFormat', filterable: false },
        { text: 'Panel', value: 'ParentContainer', filterable: false },
        { text: 'Tab', value: 'ParentTab', filterable: false },
      ],
      formElements: [],
      isDialogShown: false,

      editedItem: {},
      editedIndex: -1,
      isDialogDeleteShown: false,
      plansList: [],
      fieldTypeList: [
        'Address',
        'CheckBox',
        'Email',
        'datetime',
        'Header',
        'List',
        'Lookup',
        'LookupList',
        'numeric',
        'Panel',
        'Photo',
        'PlanList',
        'PlanLookup',
        'Radio',
        'Signature',
        'StatusHistory',
        'string',
        'Tab',
        'Telephone',
      ],
    };
  },
  computed: {
    dialogTitle() {
      return this.editedIndex === -1 ? 'Add new row' : 'Edit field';
    },
    currentFormId() {
      if (this.formElements.length > 0) return this.formElements[0].FormId;
      return null;
    },
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
    currentPlanName() {
      if (this.currentPlanId && this.plansList && this.plansList.length > 0) {
        const plan = this.plansList.find((x) => {
          return x.Id == this.currentPlanId;
        });
        if (plan) {
          return plan.ShortName;
        }
      }

      return '';
    },
    defaultItem() {
      const defaultItem = {};
      if (this.formElements.length > 0) {
        for (let prop in this.formElements[0]) {
          defaultItem[prop] = '';
        }

        defaultItem.ElementId = 0;
        defaultItem.ReadOnly = false;
        defaultItem.Visible = false;
        defaultItem.Mandatory = false;
        defaultItem.FormId = this.currentFormId;
      }
      return defaultItem;
    },
    parentTabsList() {
      return this.formElements.filter((el) => el.FieldType === 'Tab' || el.FieldType === 'TAB');
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    selectedForm() {
      this.getFormElements();
    },
    planLevel() {
      this.getFormElements();
    },
  },

  created() {
    this.editedItem = { ...this.defaultItem };
  },
  mounted() {
    this.getFormsList();
    this.getPlansList();
  },

  methods: {
    resetEdited() {
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
      this.isDialogShown = false;
    },
    editFieldRow(item) {
      if (!this.showFormButtons) return;
      this.overwritePlanLevel = false;
      this.editedIndex = this.formElements.indexOf(item);
      this.editedItem = { ...item };
      this.isDialogShown = true;
    },
    saveItem() {
      if (this.editedIndex === -1) {
        this.editedItem.ElementId = 0;
        this.editedItem.FormId = this.currentFormId;
        this.editedItem.Visible = Boolean(this.editedItem.Visible);
      }
      if (this.planLevel && this.currentPlanId > 0) {
        this.editedItem.PlanId = this.currentPlanId;
      } else {
        if (this.overwritePlanLevel){
          this.editedItem.PlanId = -this.currentPlanId;
        }
      }


      this.updateFormElement();
      this.resetEdited();
    },
    async deleteItem() {
      try {
        this.loading = false;
        await this.$api.delete('/datadictionary/deleteformfield?Id=' + this.editedItem.ElementId);

        this.getFormElements();
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Item deleted!',
        });
        this.isDialogShown = false;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
    cancelSave() {
      this.resetEdited();
    },

    async getFormsList() {
      this.loading = true;
      try {
        const response = await this.$api.get('/datadictionary/GetFormList');

        if (!response.data.length) {
          const msg = 'Unable to load form list';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }
        this.formsList = response.data;
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loading = false;
      }
    },

    async getFormElements() {
      this.loading = true;
      try {
        const planId = this.planLevel ? this.currentPlanId : 0;
        const response = await this.$api.get(
          '/datadictionary/GetFormElements?planId=' + planId + '&formName=' + this.selectedForm
        );

        this.formElements = response.data;
      } catch (err) {
        console.log(err.message);
      } finally {
        this.loading = false;
      }
    },

    async updateFormElement() {
      this.loading = true;
      try {
        await this.$api.post('/datadictionary/UpdateFormElement', this.editedItem);

        this.getFormElements();
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Saved!',
        });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async getPlansList() {
      try {
        const response = await this.$api.get('/plans/getplanlist');

        if (!response.data.length) {
          const msg = 'Unable to load plans';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        if (response.data && response.data.length) this.plansList = response.data;
      } catch (error) {
        console.log(error.message);
      }
    },
  },
};
</script>

<style scoped>
.headPanel {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 20px 0 20px;
}
.headPanel .formControl {
  width: 371px;
}

.headPanel .planLevelCheckbox {
  display: inline-block;
  margin-right: 20px;
  vertical-align: top;
}

.headPanel .currentPlanName {
  background-color: #4a1cd426;
  padding: 8px;
  color: #4a1cd4;
}

.headPanel > div {
  margin-right: 30px;
}
.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
.builderTable {
  padding: 0 20px;
}
.search {
  max-width: 475px;
}
.tableToolbar {
  display: flex;
  align-items: baseline;
  padding-bottom: 4px;
}
</style>
